/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */
import * as React from 'react';
import { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { breakpointsMap } from '@oneaudi/unified-web-common';
import {
  Layout,
  LayoutItem,
  Text,
  AccordionSection,
  Accordion,
} from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import { messages } from '../../../i18n/messages';
import { DepartmentOpeningHours, PbcPartner } from './PartnerData';
import { useMedia } from '../../../hooks/useMedia';
import { OpeningHoursDataList } from './OpeningHoursDataList';
import { OpeningHoursLabel } from './OpeningHoursLabel';
import { LayoutContainer } from './LayoutContainer';
import { throttle } from '../../../utils/throttle';
import { useTrackingManager } from '../../../hooks/useTrackingManager';

// SC

// @todo: fix sizing as soon as text is translated

const DepartmentsGrid = styled.div`
  display: flex;
  max-width: 100%;
  flex-wrap: wrap;
`;

const DepartmentsGridItem = styled.div`
  box-sizing: border-box;
  width: 318px;
  padding-bottom: 3rem;
  margin-right: 2rem;
`;

const OpeningHoursNoteContainer = styled(LayoutContainer)`
  @media (min-width: ${breakpointsMap.md}) {
    border-top: 1px solid var(#808080);
  }
`;

const OpeningHoursMainContainer = styled(LayoutContainer)`
  @media (min-width: ${breakpointsMap.md}) {
    max-width: 100%;
  }
  > div {
    border-top: none;
    > :first-child {
      border-top: none !important;
    }
  }
`;
// Components

interface OpeningHoursProps {
  data: PbcPartner;
  hasHeadline?: boolean;
  isNarrow?: boolean;
}

export const OpeningHours: React.FunctionComponent<OpeningHoursProps> = ({
  data,
  hasHeadline = true,
  isNarrow = false,
}) => {
  const isViewportM = useMedia(`only screen and (min-width: ${breakpointsMap.md}) `);
  const gridElement = useRef<HTMLDivElement>(null);
  const renderAsAccordion = !isViewportM || isNarrow;
  const toggleObj = {};
  let departments: DepartmentOpeningHours[] = [];
  let openingHoursNote;

  if (data.openingHours) {
    departments = data.openingHours.departments || [];
    openingHoursNote = data.openingHours.openingHoursNote;
    data?.openingHours?.departments.forEach((item) => {
      const key = item.departmentName;
      (toggleObj as Record<string, unknown>)[key] = false;
    });
  }

  const [toggleState, setToggleState] = React.useState<Record<string, boolean>>(toggleObj);

  useEffect(() => {
    const $gridContainer = gridElement.current;
    const $gridCellHeaders = $gridContainer?.querySelectorAll<HTMLDivElement>(
      '[data-testid*="grid-head"]',
    );
    // eslint-disable-next-line no-undef
    const columnConform = (elements: NodeListOf<HTMLDivElement> | undefined): void => {
      if (!elements) {
        return;
      }

      const elementCollection: Record<string, { height: number; elements: HTMLDivElement[] }> =
        Array.from(elements).reduce((acc, curr) => {
          // eslint-disable-next-line no-param-reassign
          curr.style.height = 'auto';
          acc[`${curr.offsetTop}`] = acc[`${curr.offsetTop}`] || {
            height: 0,
            elements: [],
          };
          acc[`${curr.offsetTop}`].height =
            curr.clientHeight > acc[`${curr.offsetTop}`].height
              ? curr.clientHeight
              : acc[`${curr.offsetTop}`].height;
          acc[`${curr.offsetTop}`].elements.push(curr);
          return acc;
        }, {} as any);

      Object.keys(elementCollection).forEach((key) =>
        elementCollection[key].elements.forEach((el) => {
          // eslint-disable-next-line no-param-reassign
          el.style.height = `${elementCollection[key].height}px`;
        }),
      );
    };
    columnConform($gridCellHeaders);
    const throttledColumnConform = throttle(() => {
      columnConform($gridCellHeaders);
    }, 200);
    window.addEventListener('resize', throttledColumnConform);
    return () => window.removeEventListener('resize', throttledColumnConform);
  }, []);

  const now = new Date();
  const trackingManager = useTrackingManager();

  return (
    <OpeningHoursMainContainer>
      {hasHeadline && (
        <Text
          tag="span"
          variant={{
            order: '3',
            type: 'copy',
            style: 'normal',
          }}
          data-testid="openingHours-headline"
        >
          {useI18n(messages.openingHoursHeadline)}
        </Text>
      )}
      {renderAsAccordion && (
        <Accordion>
          {departments.map((department: DepartmentOpeningHours, i: number) => (
            <AccordionSection
              label={(<OpeningHoursLabel department={department} now={now} isNarrow />) as any}
              headingTag="span"
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              open={toggleState[department.departmentName]}
              onToggle={() => {
                trackingManager.click(
                  'content',
                  '',
                  'accordion',
                  department.departmentName,
                  data.nameEnhanced,
                  (i + 1).toString(),
                  department.departmentName,
                );

                setToggleState({
                  ...toggleState,
                  [department.departmentName]: !toggleState[department.departmentName],
                });
              }}
            >
              <Text margin={[0, 0, 0, 0]}>{department.departmentOpeningHoursNote}</Text>
              <OpeningHoursDataList department={department} now={now} />
            </AccordionSection>
          ))}
        </Accordion>
      )}
      {isViewportM && !isNarrow && (
        <DepartmentsGrid ref={gridElement}>
          {departments?.map((department: DepartmentOpeningHours, i: number) => (
            // eslint-disable-next-line react/no-array-index-key
            <DepartmentsGridItem key={i}>
              <Layout direction="column">
                <LayoutItem data-testid={`grid-head-${i}`}>
                  <OpeningHoursLabel department={department} now={now} />
                  <Text margin={['12px', 0, '24px', 0]}>
                    {department.departmentOpeningHoursNote}
                  </Text>
                </LayoutItem>
                <LayoutItem>
                  <OpeningHoursDataList department={department} now={now} />
                </LayoutItem>
              </Layout>
            </DepartmentsGridItem>
          ))}
        </DepartmentsGrid>
      )}
      {openingHoursNote && (
        <OpeningHoursNoteContainer>
          <Text variant={{ order: '2', style: 'normal', type: 'copy' }} margin={['16px', 0, 0, 0]}>
            {openingHoursNote}
          </Text>
        </OpeningHoursNoteContainer>
      )}
    </OpeningHoursMainContainer>
  );
};
